const cache = new Map<string, HTMLAudioElement>();

// 0.05s of silence — a real, decodable source. Needed because play() on a
// src-less Audio element always rejects and therefore unlocks nothing.
const SILENT_WAV =
  "data:audio/wav;base64,UklGRiQAAABXQVZFZm10IBAAAAABAAEAgD4AAAB9AAACABAAZGF0YQAAAAA=";

let audioUnlocked = false;

function getAudio(url: string): HTMLAudioElement {
  let audio = cache.get(url);
  if (!audio) {
    audio = new Audio(url);
    audio.preload = "auto";
    audio.load();
    cache.set(url, audio);
  }
  return audio;
}

/**
 * Create and load the audio elements ahead of the first notification, while a
 * user gesture is still likely to be available.
 */
export function preloadNotificationSounds(
  urls: Array<string | null | undefined>
): void {
  urls.forEach((url) => {
    if (url) getAudio(url);
  });
}

export function playNotificationSound(url: string | null | undefined): void {
  if (!url) {
    console.warn("[notificationSound] no sound_url configured for this event");
    return;
  }

  const audio = getAudio(url);

  try {
    audio.currentTime = 0;
  } catch {
    // some browsers throw if audio not yet loaded; ignore
  }

  audio.play().catch((err: DOMException) => {
    if (err?.name === "NotAllowedError") {
      console.warn(
        "[notificationSound] blocked by autoplay policy — the tab has no user activation yet:",
        url
      );
    } else {
      console.warn(
        `[notificationSound] failed to play (${err?.name}: ${err?.message})`,
        url,
        "networkState:",
        audio.networkState,
        "readyState:",
        audio.readyState
      );
    }
  });
}

export function primeAudioUnlock(): void {
  if (audioUnlocked) return;

  const unlock = () => {
    const a = new Audio(SILENT_WAV);
    a.muted = true;
    a.play()
      .then(() => {
        audioUnlocked = true;
        // Warm every already-known sound inside the gesture so later playback
        // is treated as a resumption rather than a fresh autoplay attempt.
        cache.forEach((audio) => audio.load());
      })
      .catch((err: DOMException) => {
        console.warn(
          `[notificationSound] audio unlock failed (${err?.name}: ${err?.message})`
        );
      });

    document.removeEventListener("click", unlock);
    document.removeEventListener("keydown", unlock);
    document.removeEventListener("touchstart", unlock);
  };

  document.addEventListener("click", unlock, { once: true });
  document.addEventListener("keydown", unlock, { once: true });
  document.addEventListener("touchstart", unlock, { once: true });
}
